import { z } from 'zod';
import {
  ActivePartyResponseSchema,
  FriendRequestsResponseSchema,
  FriendRequestSchema,
  FriendsResponseSchema,
  LeaderboardResponseSchema,
  PartySchema,
  RunRecordSchema,
  UserSchema,
  UserSettingsSchema,
  communityRoutes,
} from '@garmoth/community-contracts';
import { request } from './client';

// Response wrappers match the API. Parsing them here keeps page code independent
// of HTTP details and makes a mismatched deployment fail visibly.
const UserResponseSchema = z.strictObject({ user: UserSchema });
const PartyResponseSchema = z.strictObject({ party: PartySchema });
const FriendRequestResponseSchema = z.strictObject({ request: FriendRequestSchema });
const FriendResponseSchema = z.strictObject({ friend: UserSchema });
const RunListResponseSchema = z.strictObject({ runs: z.array(RunRecordSchema) });
const LiveTicketResponseSchema = z.strictObject({ ticket: z.string().min(20), expiresAt: z.iso.datetime() });
const DeviceConfirmResponseSchema = z.strictObject({ approved: z.literal(true) });
const VoidSchema = z.undefined();

export const api = {
  me: async (signal?: AbortSignal) =>
    (await request(communityRoutes.me, UserResponseSchema, { signal })).user,
  updateSettings: async (settings: z.infer<typeof UserSettingsSchema>) =>
    (await request(`${communityRoutes.me}/settings`, UserResponseSchema, { method: 'PATCH', body: settings })).user,
  signOut: () => request('/auth/logout', VoidSchema, { method: 'POST' }),

  leaderboard: (spotName: string | undefined, signal?: AbortSignal) =>
    request(communityRoutes.leaderboard, LeaderboardResponseSchema, {
      signal, query: { scope: 'global', spotName },
    }),

  activeParty: (signal?: AbortSignal) => request(communityRoutes.parties, ActivePartyResponseSchema, { signal }),
  createParty: async () => (await request(communityRoutes.parties, PartyResponseSchema, { method: 'POST' })).party,
  joinParty: async (inviteCode: string) =>
    (await request(communityRoutes.joinParty, PartyResponseSchema, { method: 'POST', body: { inviteCode } })).party,
  leaveParty: (partyId: string) =>
    request(`${communityRoutes.parties}/${encodeURIComponent(partyId)}/leave`, VoidSchema, { method: 'POST' }),
  liveTicket: (partyId: string) =>
    request(`${communityRoutes.parties}/${encodeURIComponent(partyId)}/live-ticket`, LiveTicketResponseSchema, { method: 'POST' }),
  confirmDevice: (userCode: string) =>
    request(communityRoutes.deviceConfirm, DeviceConfirmResponseSchema, { method: 'POST', body: { userCode } }),

  friends: (signal?: AbortSignal) => request(communityRoutes.friends, FriendsResponseSchema, { signal }),
  removeFriend: (discordId: string) =>
    request(`${communityRoutes.friends}/${encodeURIComponent(discordId)}`, VoidSchema, { method: 'DELETE' }),
  friendRequests: (signal?: AbortSignal) =>
    request(communityRoutes.friendRequests, FriendRequestsResponseSchema, { signal }),
  requestFriend: async (discordUsername: string) =>
    (await request(communityRoutes.friendRequests, FriendRequestResponseSchema, { method: 'POST', body: { discordUsername } })).request,
  acceptFriendRequest: async (id: string) =>
    (await request(`${communityRoutes.friendRequests}/${encodeURIComponent(id)}/accept`, FriendResponseSchema, { method: 'POST' })).friend,
  declineFriendRequest: (id: string) =>
    request(`${communityRoutes.friendRequests}/${encodeURIComponent(id)}/decline`, VoidSchema, { method: 'POST' }),

  runs: (signal?: AbortSignal) => request(communityRoutes.runs, RunListResponseSchema, { signal }),
};
